import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Alert, Spinner, Container } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const AddNewContainer = ({ setActiveView }) => {
  const [formData, setFormData] = useState({
    containerTypeName: '',
    friendlyName: '',
    measureMax: '',
    measureIncrement: '',
    imageURL: '',
    uomId: '',
    lowLevelWarning1Enable: false,
    lowLevelWarning2Enable: false,
    lowLevelWarning3Enable: false,
    lowLevel1Warning: '',
    lowLevel2Warning: '',
    lowLevel3Warning: ''
  });

  const [uoms, setUoms] = useState([]);
  const [uomsLoading, setUomsLoading] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, verifySession, logout } = useAuth();
  const navigate = useNavigate();

  // Fetch UOMs on load
  useEffect(() => {
    const loadUoms = async () => {
      try {
        setUomsLoading(true);
        await verifySession();
        const res = await axios.get('/uoms', { withCredentials: true });
        const list = res.data?.data || res.data || [];
        setUoms(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading UOMs:', err);
        if (err.response?.status === 401) logout();
        else setError(err.response?.data?.message || 'Failed to load UOMs');
      } finally {
        setUomsLoading(false);
      }
    };
    if (isAuthenticated) loadUoms();
  }, [isAuthenticated, verifySession, logout]);

  // Helper: determine if selected UOM is "each - A single, individual item"
  const isEachUomSelected = (() => {
    const sel = uoms.find(u => u.id === formData.uomId);
    if (!sel) return false;
    const label = (sel.label || '').trim().toLowerCase();
    const desc  = (sel.description || '').trim().toLowerCase();
    // Robust checks: exact string, or label starts with "each"
    return (
      `${label} - ${desc}` === 'each - a single, individual item' ||
      label === 'each' ||
      label.startsWith('each ')
    );
  })();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // When UOM changes to EACH, clear measure fields and hide them
    if (name === 'uomId') {
      const nextUomId = value;
      const sel = uoms.find(u => u.id === nextUomId);
      const nextLabel = (sel?.label || '').trim().toLowerCase();
      const nextDesc  = (sel?.description || '').trim().toLowerCase();
      const nextIsEach =
        `${nextLabel} - ${nextDesc}` === 'each - a single, individual item' ||
        nextLabel === 'each' ||
        nextLabel.startsWith('each ');

      setFormData(prev => ({
        ...prev,
        uomId: nextUomId,
        // If switching to EACH, clear the measure fields so nothing stale gets posted
        measureMax: nextIsEach ? '' : prev.measureMax,
        measureIncrement: nextIsEach ? '' : prev.measureIncrement,
      }));
      return;
    }

    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const validateWarnings = () => {
    for (let level = 1; level <= 3; level++) {
      if (
        formData[`lowLevelWarning${level}Enable`] &&
        (formData[`lowLevel${level}Warning`] === '' ||
         !Number.isFinite(Number(formData[`lowLevel${level}Warning`])))
      ) {
        setError(`Level ${level} Warning Value must be a valid number and not empty if enabled.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.containerTypeName.trim() || !formData.friendlyName.trim()) {
      setError('Container Type Name and Friendly Name are required.');
      return;
    }
    if (!validateWarnings()) return;

    try {
      setLoading(true);
      await verifySession();

      // Base payload (do NOT include measure fields if EACH UOM is selected)
      const payload = {
        containerTypeName: formData.containerTypeName,
        friendlyName: formData.friendlyName,
        imageURL: formData.imageURL || undefined,
        uomId: formData.uomId || null,
        lowLevelWarning1Enable: formData.lowLevelWarning1Enable,
        lowLevelWarning2Enable: formData.lowLevelWarning2Enable,
        lowLevelWarning3Enable: formData.lowLevelWarning3Enable,
        lowLevel1Warning: formData.lowLevel1Warning !== '' ? Number(formData.lowLevel1Warning) : undefined,
        lowLevel2Warning: formData.lowLevel2Warning !== '' ? Number(formData.lowLevel2Warning) : undefined,
        lowLevel3Warning: formData.lowLevel3Warning !== '' ? Number(formData.lowLevel3Warning) : undefined
      };

      if (!isEachUomSelected) {
        // Only include when not "each"
        if (formData.measureMax !== '') payload.measureMax = Number(formData.measureMax);
        if (formData.measureIncrement !== '') payload.measureIncrement = Number(formData.measureIncrement);
      }

      await axios.post('/container-types', payload, { withCredentials: true });

      setSuccess('Container type added successfully!');
      setTimeout(() => {
        if (typeof setActiveView === 'function') setActiveView('containerTypes');
        navigate('/dashboard/container-types');
      }, 1200);
    } catch (err) {
      console.error('Error adding container:', err);
      if (err.response?.status === 401) {
        logout();
      } else {
        setError(err.response?.data?.message || 'Failed to add container type');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <Container className="content-container">
      <h2 className="mb-4">Add New Container</h2>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Container Type Name *</Form.Label>
          <Form.Control
            type="text"
            name="containerTypeName"
            value={formData.containerTypeName}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Friendly Name *</Form.Label>
          <Form.Control
            type="text"
            name="friendlyName"
            value={formData.friendlyName}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </Form.Group>

        {/* Container UOM */}
        <Form.Group className="mb-3">
          <Form.Label>Container UOM</Form.Label>
          <Form.Select
            name="uomId"
            value={formData.uomId || ''}
            onChange={handleChange}
            disabled={loading || uomsLoading || uoms.length === 0}
          >
            <option value="">
              {uomsLoading ? 'Loading UOMs…' : (uoms.length ? 'Select UOM (optional)' : 'No UOMs available')}
            </option>
            {uoms.map(u => (
              <option key={u.id} value={u.id}>
                {u.label}{u.description ? ` - ${u.description}` : ''}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Select the unit of measure associated with this container.
            {isEachUomSelected ? ' (Note: “each” hides measurement fields.)' : ''}
          </Form.Text>
        </Form.Group>

        {/* Show measure fields ONLY when UOM is not "each" */}
        {!isEachUomSelected && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Measure Max</Form.Label>
              <Form.Control
                type="number"
                name="measureMax"
                value={formData.measureMax}
                onChange={handleChange}
                disabled={loading}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Measure Increment</Form.Label>
              <Form.Control
                type="number"
                name="measureIncrement"
                value={formData.measureIncrement}
                onChange={handleChange}
                disabled={loading}
              />
            </Form.Group>
          </>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Image URL</Form.Label>
          <Form.Control
            type="text"
            name="imageURL"
            value={formData.imageURL}
            onChange={handleChange}
            disabled={loading}
          />
        </Form.Group>

        {[1, 2, 3].map((level) => (
          <React.Fragment key={level}>
            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                name={`lowLevelWarning${level}Enable`}
                checked={formData[`lowLevelWarning${level}Enable`] || false}
                onChange={handleChange}
                label={`Enable Level ${level} Warning`}
                disabled={loading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Level {level} Warning Value</Form.Label>
              <Form.Control
                type="number"
                name={`lowLevel${level}Warning`}
                value={formData[`lowLevel${level}Warning`] || ''}
                onChange={handleChange}
                disabled={loading}
              />
            </Form.Group>
          </React.Fragment>
        ))}

        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              if (typeof setActiveView === 'function') setActiveView('containerTypes');
              navigate('/dashboard/container-types');
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner as="span" size="sm" animation="border" role="status" />
                <span className="ms-2">Saving...</span>
              </>
            ) : 'Save Container'}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default AddNewContainer;